import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '../../../design-system/components/form/button/Button';
import { InputRadio } from '../../../design-system/components/form/inputRadio/InputRadio';
import { IconSort } from '../../../design-system/components/icons/Sort';
import { useOutsideClick } from '../../../shared/hooks/useOutsideClick';

import style from './Sort.module.css';

type SortProps = {
    onSortSelected: (value: SortValue) => void;
};

type option = { name: string; value: SortValue };

export type SortValue = 'pertinence' | 'pricingAsc' | 'pricingDesc';

export function Sort({ onSortSelected }: SortProps) {
    const [selected, setSelected] = useState<SortValue>('pertinence');
    const [showOptions, setShowOptions] = useState(false);
    const { formatMessage } = useIntl();

    const ref = useRef<HTMLDivElement>(null);
    useOutsideClick(ref, () => setShowOptions(false));

    const options: Array<option> = useMemo(
        () => [
            {
                name: formatMessage({ id: 'page.itinerariesList.sort.pertinence' }),
                value: 'pertinence',
            },
            {
                name: formatMessage({ id: 'page.itinerariesList.sort.desc' }),
                value: 'pricingDesc',
            },
            {
                name: formatMessage({ id: 'page.itinerariesList.sort.asc' }),
                value: 'pricingAsc',
            },
        ],
        [formatMessage],
    );

    const toggleSortOptions = useCallback(() => {
        setShowOptions(!showOptions);
    }, [showOptions]);

    const selectSortOption = useCallback(
        (option: option) => {
            setSelected(option.value);
            onSortSelected(option.value);
        },
        [onSortSelected],
    );

    return (
        <div className={style.wrapper}>
            <Button priority="text" iconPosition="left" Icon={IconSort} onClick={toggleSortOptions}>
                {formatMessage({ id: 'page.itinerariesList.sort.cta' })}
            </Button>
            {showOptions ? (
                <div ref={ref} className={style.optionsWrapper}>
                    {options.map((option) => (
                        <InputRadio
                            key={option.value}
                            inputProps={{
                                name: option.name,
                                value: option.value,
                                checked: selected === option.value,
                                onChange: () => selectSortOption(option),
                            }}
                            label={option.name}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
}
